import { useState, type ReactElement } from 'react';
import type { CabinApplyTarget } from './cabinEditorTypes';

export interface ActionBarProps {
  readonly target: CabinApplyTarget;
  /** The layout has no validation errors. */
  readonly canApply: boolean;
  /** The layout differs from the baseline. */
  readonly isDirty: boolean;
  /** Message describing the last command sent, shown politely to screen readers. */
  readonly statusMessage: string | null;
  readonly onApply: () => void;
  readonly onReset: () => void;
}

/**
 * Apply / reset actions. Applying sends a command that spends money (an
 * acquisition) or changes a live aircraft (a refit), so it takes two clicks.
 */
export function ActionBar({
  target,
  canApply,
  isDirty,
  statusMessage,
  onApply,
  onReset,
}: ActionBarProps): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const isAcquisition = target.kind === 'ACQUISITION';
  const applyLabel = isAcquisition ? 'Acquire with this cabin' : 'Apply to Fleet Instance';
  // A fleet refit with no changes has nothing to send; an acquisition always does.
  const applyDisabled = !canApply || (!isAcquisition && !isDirty);

  const handleApplyClick = (): void => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onApply();
  };

  return (
    <div className="ce-actions">
      <div className="ce-actions__buttons">
        {confirming ? (
          <>
            <button
              type="button"
              className="ce-btn ce-btn--primary"
              disabled={applyDisabled}
              onClick={handleApplyClick}
            >
              Confirm: {applyLabel}
            </button>
            <button type="button" className="ce-btn ce-btn--ghost" onClick={(): void => setConfirming(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="ce-btn ce-btn--primary"
              disabled={applyDisabled}
              onClick={handleApplyClick}
            >
              {applyLabel}
            </button>
            <button type="button" className="ce-btn ce-btn--ghost" disabled={!isDirty} onClick={onReset}>
              Reset changes
            </button>
          </>
        )}
      </div>
      <p className="ce-actions__status" role="status">
        {statusMessage}
      </p>
    </div>
  );
}
